import React, { useEffect, useState, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import './pay-invoice.css';
import { FaFastBackward, FaDownload } from 'react-icons/fa';
import { SlPrinter } from 'react-icons/sl';
import { useNavigate, useParams } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import html2canvas from 'html2canvas';
 
export default function PayInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [payroll, setPayroll] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pdfRef = useRef();
 
  useEffect(() => {
    const fetchPayroll = async () => {
      try {
        const response = await axios.get(`https://backend.ems.hiradentallab.com/api/payroll/payroll/${id}`);
        setPayroll(response.data.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch payroll data');
      } finally {
        setLoading(false);
      }
    };
 
    fetchPayroll();
  }, [id]);
 
  const handleDownloadPDF = () => {
    if (!payroll) return;
   
    const doc = new jsPDF('p', 'pt', 'a4');
    const pdfContent = pdfRef.current;
   
    html2canvas(pdfContent, {
      scale: 2,
      logging: false,
      useCORS: true
    }).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = doc.internal.pageSize.getWidth() - 40;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
     
      doc.addImage(imgData, 'PNG', 20, 20, imgWidth, imgHeight);
      doc.save(`payslip-${payroll.username}.pdf`);
    });
  };
 
  const handlePrint = () => {
    window.print();
  };
 
  const handleGoBack = () => {
    navigate('/payroll-report');
  };
 
  const formatDate = (dateString) => {
    return format(parseISO(dateString), 'MM/dd/yyyy');
  };
 
  const formatCurrency = (amount) => {
    return amount?.toLocaleString('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 }) || 'PKR 0';
  };
 
  const calculateTotalDeductions = () => {
    if (!payroll) return 0;
    return (
      (payroll.lateMinutesDeduction || 0) +
      (payroll.earlyCloseDeduction || 0) +
      (payroll.leaveDeduction || 0) +
      (payroll.taxDeduction?.amount || 0) +
      (payroll.providentFundContribution || 0) // Changed from payroll.providentFundContribution?.amount
    );
  };
 
  const calculateTotalEarnings = () => {
    if (!payroll) return 0;
    return (
      (payroll.extraMinutesBonus || 0) +
      (payroll.publicHolidayPay || 0) +
      (payroll.workingUnits * payroll.unitPayRate || 0) + // Calculate workingUnitsPay dynamically
      (payroll.adjustments?.amount || 0) +
      (payroll.providentFund?.amount || 0)
    );
  };
 
  if (loading) return <Container className="text-center mt-5"><p>Loading...</p></Container>;
  if (error) return <Container className="text-center mt-5 text-danger"><p>{error}</p></Container>;
  if (!payroll) return <Container className="text-center mt-5"><p>No payroll data found</p></Container>;
 
  return (
    <>
      <Container fluid className="action-buttons mb-4">
        <Row className="justify-content-end mb-4 mt-4">
          <Col xs="auto">
            <Button className="action-button" variant="outline-danger" onClick={handleGoBack}>
              <FaFastBackward /> Back
            </Button>
          </Col>
          <Col xs="auto">
            <Button className="action-button" variant="outline-success" onClick={handlePrint}>
              <SlPrinter /> Print
            </Button>
          </Col>
          <Col xs="auto">
            <Button className="action-button" variant="outline-primary" onClick={handleDownloadPDF}>
              <FaDownload /> Download as PDF
            </Button>
          </Col>
        </Row>
      </Container>
 
      <div ref={pdfRef} className="payslip-container">
        <Container fluid className="payslip-wrapper">
          {/* Header Section */}
          <div className="payslip-header text-center mb-4">
            <h2 className="company-name">HIRA DENTAL LAB</h2>
            <h3 className="payslip-title">EMPLOYEE PAYSLIP</h3>
            <p className="pay-period">
              Pay Period: {format(parseISO(payroll.startDate), 'MMMM dd, yyyy')} to {format(parseISO(payroll.endDate), 'MMMM dd, yyyy')}
            </p>
          </div>
         
          {/* Employee Information Section */}
          <div className="employee-info-section mb-4">
            <Row>
              <Col md={6}>
                <div className="info-item">
                  <span className="info-label">Employee ID:</span>
                  <span className="info-value">{payroll.username}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Employee Name:</span>
                  <span className="info-value">{payroll.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Total Salary:</span>
                  <span className="info-value">{formatCurrency(payroll.salary)}</span>
                </div>
              </Col>
              <Col md={6}>
                <div className="info-item">
                  <span className="info-label">Department:</span>
                  <span className="info-value">Information Technology</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Designation:</span>
                  <span className="info-value">Employee</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Working Branch:</span>
                  <span className="info-value">Lahore</span>
                </div>
              </Col>
            </Row>
          </div>
         
          {/* Earnings and Deductions Section */}
          <div className="earnings-deductions-section mb-4">
            <Row>
              <Col md={6} className="pe-md-3">
                <div className="earnings-box">
                  <h5 className="section-title">Additional Earnings</h5>
                  <table className="earnings-table">
                    <tbody>
                      {payroll.extraMinutesBonus > 0 && (
                        <tr>
                          <td>Overtime ({(payroll.extraMinutesBonus / 60).toFixed(2)} hours)</td>
                          <td className="text-end">{formatCurrency(payroll.extraMinutesBonus)}</td>
                        </tr>
                      )}
                      {payroll.publicHolidayPay > 0 && (
                        <tr>
                          <td>Public Holiday Pay</td>
                          <td className="text-end">{formatCurrency(payroll.publicHolidayPay)}</td>
                        </tr>
                      )}
                      {(payroll.workingUnits * payroll.unitPayRate) > 0 && (
                        <tr>
                          <td>Working Units ({payroll.workingUnits || 0} units)</td>
                          <td className="text-end">{formatCurrency(payroll.workingUnits * payroll.unitPayRate)}</td>
                        </tr>
                      )}
                      {payroll.adjustments?.amount > 0 && (
                        <tr>
                          <td>Adjustments</td>
                          <td className="text-end">{formatCurrency(payroll.adjustments?.amount)}</td>
                        </tr>
                      )}
                      {payroll.providentFund?.amount > 0 && (
                        <tr>
                          <td>Provident Fund Withdrawal</td>
                          <td className="text-end">{formatCurrency(payroll.providentFund?.amount)}</td>
                        </tr>
                      )}
                      <tr className="total-row">
                        <td>Total Additional Earnings</td>
                        <td className="text-end">{formatCurrency(calculateTotalEarnings())}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Col>
             
              <Col md={6} className="ps-md-3">
                <div className="deductions-box">
                  <h5 className="section-title">Deductions</h5>
                  <table className="deductions-table">
                    <tbody>
                      {payroll.lateMinutesDeduction > 0 && (
                        <tr>
                          <td>Late Minutes Deduction</td>
                          <td className="text-end">{formatCurrency(payroll.lateMinutesDeduction)}</td>
                        </tr>
                      )}
                      {payroll.earlyCloseDeduction > 0 && (
                        <tr>
                          <td>Early Close Deduction</td>
                          <td className="text-end">{formatCurrency(payroll.earlyCloseDeduction)}</td>
                        </tr>
                      )}
                      {payroll.leaveDeduction > 0 && (
                        <tr>
                          <td>Leave Deduction</td>
                          <td className="text-end">{formatCurrency(payroll.leaveDeduction)}</td>
                        </tr>
                      )}
                      {payroll.taxDeduction?.amount > 0 && (
                        <tr>
                          <td>{payroll.taxDeduction?.description || 'Tax Deduction'}</td>
                          <td className="text-end">{formatCurrency(payroll.taxDeduction?.amount)}</td>
                        </tr>
                      )}
                      {payroll.providentFundContribution > 0 && (
                        <tr>
                          <td>Provident Fund Contribution</td>
                          <td className="text-end">{formatCurrency(payroll.providentFundContribution)}</td>
                        </tr>
                      )}
                      <tr className="total-row">
                        <td>Total Deductions</td>
                        <td className="text-end">{formatCurrency(calculateTotalDeductions())}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Col>
            </Row>
          </div>
         
          {/* Summary Section */}
          <div className="summary-section mb-4">
            <Row>
              <Col md={6}>
                <div className="net-pay-box">
                  <div className="net-pay-label">Net Pay:</div>
                  <div className="net-pay-amount">{formatCurrency(payroll.finalSalary)}</div>
                </div>
              </Col>
            </Row>
          </div>
         
          {/* Footer Section */}
          <div className="payslip-footer mt-5">
            <div className="footer-note text-center mt-3">
              <p>This is computer generated payslip and does not require signature.</p>
              <p className="print-date">Generated on: {format(new Date(), 'MMMM dd, yyyy hh:mm a')}</p>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
} 